"use client"

/**
 * Finance → Settings → Setup (IBA-119).
 *
 * The branch states its finance identity — currency, fiscal-year start, timezone, and the day Ibada Cloud
 * becomes the system of record — and only then do its books open. Timezone is the branch clock (there is no
 * separate finance timezone); changing it here updates `Branch.timezone`.
 *
 * Before this, a church could record real money into a branch that had never been configured: the currency
 * defaulted to USD, the fiscal year to January, the calendar was invented on the first posting, and there was
 * no conversion date at all. A default is a seed for this form to OFFER. It is not an answer to a question
 * nobody asked.
 *
 * The gate itself is NOT here — it is at the ledger, in the API. This screen only lets a church answer the
 * questions; refusing money until it has is the backend's job, because a deep link, a stale tab or a `curl`
 * never sees a frontend redirect.
 */

import { useEffect, useState } from "react"
import { Link, useLocation } from "react-router-dom"

import { CheckCircle2Icon, LockIcon, SettingsIcon } from "lucide-react"
import { toast } from "sonner"

import { Currency } from "@ibada/shared-types/finance"
import { AccountingTimezone, isSupportedTimezone } from "@ibada/shared-types/period"
import { Button, HelperMessage, Select, SurfaceCard, type SelectOption } from "@ibada/ui"

import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { useLocale, useTranslations } from "@/i18n"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { AlivePillTone } from "@/shared/components/alive-cells"
import {
  FinanceSettingsSummaryCard,
  FinanceSettingsTile,
  FinanceSettingsTileGrid,
} from "@/features/finance/components/settings/finance-settings-tiles"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import {
  CURRENT_YEAR,
  YEAR_OPTIONS,
  monthOptions,
  conversionDayInTimezone,
  defaultConversionDay,
  monthsSinceConversion,
  openingBalanceAsAt,
  timezoneCity,
  toConversionDay,
} from "@/features/finance/lib/conversion-date"
import { getCurrencyPrefix } from "@/features/finance/lib/finance-utils"
import { pluralKey } from "@/features/finance/lib/plural-key"
import { FinanceCategoryScope } from "@/features/finance/model/finance-types"
import { FiscalPeriodStatus, type FiscalCalendar } from "@/features/finance/model/finance-close-trust-types"
import { FinanceSettingsTab } from "@/features/finance/model/finance-tabs"
import {
  useFiscalCalendarQuery,
  useOpeningBalancesStateQuery,
} from "@/features/finance/services/finance-close-trust-query"
import {
  useCompleteFinanceSetupMutation,
  useFinanceSetupQuery,
} from "@/features/finance/services/finance-setup-api"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"
import { MobilePageSkeleton } from "@/shared/components/mobile-page-skeleton"

const CURRENCY_OPTIONS: SelectOption<Currency>[] = Object.values(Currency).map((currency) => ({
  value: currency,
  label: `${currency} (${getCurrencyPrefix(currency)})`,
}))

const TIMEZONE_OPTIONS: SelectOption<AccountingTimezone>[] = Object.values(AccountingTimezone).map((zone) => ({
  value: zone,
  label: zone.replace(/_/g, " "),
}))

const labelOf = <TValue extends string>(options: SelectOption<TValue>[], value: string | null) =>
  value ? String(options.find((option) => option.value === value)?.label ?? value) : null

/** The fiscal year holding today, else the latest: what "this year's periods" means on the Ensuite row. */
function currentFiscalYear(calendar: FiscalCalendar | undefined, now = new Date()) {
  if (!calendar || calendar.years.length === 0) return null
  return (
    calendar.years.find(
      (year) => new Date(year.startDate).valueOf() <= now.valueOf() && new Date(year.endDate).valueOf() >= now.valueOf(),
    ) ?? calendar.years[0]
  )
}

export function FinanceSetupPageContent() {
  const t = useTranslations("finance.setup")
  const locale = useLocale()
  const isMobile = useIsMobile()
  // The months in the app's language, not the browser's.
  const MONTH_OPTIONS = monthOptions(locale)
  const toastError = useApiErrorToast("finance")
  const { workspaceId, activeContext, scopeBranch, parishBranch, canEditFinance } = useFinancePageState()
  const branchId = scopeBranch?.id ?? null
  const branchTimezone = scopeBranch?.timezone ?? null
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug() ?? undefined
  const { search } = useLocation()

  const setupQuery = useFinanceSetupQuery(workspaceId ?? null, branchId)
  const completeMutation = useCompleteFinanceSetupMutation(workspaceId ?? null, branchId)

  const status = setupQuery.data ?? null
  // The "Ensuite" row: the year's periods and the opening balances, read from the queries their own tabs use.
  const showNext = Boolean(status?.isComplete)
  const calendarQuery = useFiscalCalendarQuery(showNext ? workspaceId ?? null : null, branchId)
  const openingScope = activeContext === "branch" ? FinanceCategoryScope.Branch : FinanceCategoryScope.Parish
  const openingQuery = useOpeningBalancesStateQuery(
    showNext && workspaceId && branchId ? { workspaceId, branchId, scope: openingScope } : null,
  )

  const [currency, setCurrency] = useState<Currency | null>(null)
  const [startMonth, setStartMonth] = useState<string | null>(null)
  const [conversionDate, setConversionDate] = useState<string | null>(null)
  const [timezone, setTimezone] = useState<AccountingTimezone | null>(null)

  // Follow the server once it answers; a choice the user is in the middle of making always wins over a refetch.
  useEffect(() => {
    if (!status) return
    setCurrency((current) => current ?? status.baseCurrency)
    setStartMonth((current) => current ?? String(status.fiscalYearStartMonth))
    setTimezone((current) =>
      current ??
      (branchTimezone && isSupportedTimezone(branchTimezone)
        ? branchTimezone
        : AccountingTimezone.AfricaKigali),
    )
    setConversionDate((current) =>
      current ?? (branchTimezone
        ? conversionDayInTimezone(status.conversionDate, branchTimezone) || defaultConversionDay()
        : defaultConversionDay()),
    )
  }, [branchTimezone, status])

  if (setupQuery.isLoading) {
    return isMobile ? <MobilePageSkeleton header={false} groups={[3, 3]} /> : <p className='text-muted-foreground text-sm'>{t("loading")}</p>
  }
  if (!status || !branchId || !workspaceId) {
    return (
      <HelperMessage tone='warning' size='md'>
        {t("noBranch")}
      </HelperMessage>
    )
  }

  const locked = status.isLocked
  // Cascade: a branch inherits the parish's fiscal year (read-only) and cannot configure at all until the
  // parish is set up (the backend enforces both; the form reflects them).
  const fiscalYearInherited = Boolean(status.fiscalYearInherited)
  const parishBlocked = !status.parishSetupComplete
  // Currency (ADR-0022 §6): editable by a SELF-CONFIGURING context (parish / mono-branch) while the ledger is
  // empty; a cascading branch inherits the parish's (read-only), and any branch locks once it has posted.
  const currencyEditable = canEditFinance && !locked && !parishBlocked && !fiscalYearInherited
  const timezoneEditable = canEditFinance && !locked && !parishBlocked
  const startMonthEditable = canEditFinance && !locked && !fiscalYearInherited && !parishBlocked
  const dateEditable = canEditFinance && !locked && !parishBlocked
  const canSubmit =
    canEditFinance &&
    !locked &&
    !parishBlocked &&
    Boolean(currency && startMonth && conversionDate && timezone) &&
    !completeMutation.isPending

  // The conversion date is stored as `YYYY-MM-01`; the Month/Year selects read and rewrite those parts.
  const conversionYear = conversionDate ? conversionDate.slice(0, 4) : null
  const conversionMonth = conversionDate ? String(Number(conversionDate.slice(5, 7))) : null
  const openingAsAt = openingBalanceAsAt(conversionDate, locale)
  const city = timezoneCity(timezone)
  const conversionMonthYear = conversionDate
    ? new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(
        new Date(Date.UTC(Number(conversionYear), Number(conversionMonth) - 1, 1)),
      )
    : null
  const startIndex = Number(startMonth ?? 1)
  const endMonth = String(((startIndex + 10) % 12) + 1)
  // Mid-sentence month names as the language writes them ("janvier" in French, "January" in English).
  const monthInSentence = (month: number) =>
    new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2026, month - 1, 15)))
  const monthsKept = status.isComplete ? monthsSinceConversion(conversionDate) : null

  const onSubmit = async () => {
    if (!canEditFinance || parishBlocked) return
    if (!currency || !startMonth || !conversionDate || !timezone) return
    try {
      await completeMutation.mutateAsync({
        // ADR-0022 §6: send the chosen currency only where it is editable; a cascading branch inherits it and
        // the backend ignores anything sent, but omitting it keeps the intent honest.
        ...(currencyEditable ? { baseCurrency: currency } : {}),
        timezone,
        fiscalYearStartMonth: Number(startMonth),
        conversionDate,
      })
      toast.success(t("toasts.saved"))
    } catch (error) {
      toastError(error)
    }
  }

  // One sentence: when, in what, on whose clock, and why it no longer moves (or what is still missing).
  const description = parishBlocked
    ? t("parishFirst")
    : status.isComplete
      ? [
          conversionMonthYear && currency && city
            ? t("summary.since", { month: conversionMonthYear, currency, city })
            : null,
          locked ? t(pluralKey("summary.lockedReason", status.postedEntries), { count: status.postedEntries }) : t("summary.stillOpen"),
        ]
          .filter(Boolean)
          .join(" ")
      : t("summary.incomplete")

  const lockedLabel = t("tiles.locked")
  const select = <TValue extends string>(
    label: string,
    options: SelectOption<TValue>[],
    value: string | null,
    onChange: (value: TValue) => void,
    searchable = false,
  ) => (
    <Select
      aria-label={label}
      options={options}
      value={(value ?? undefined) as TValue | undefined}
      onValueChange={(next) => onChange(next as TValue)}
      searchEnabled={searchable}
    />
  )

  // The Ensuite row: where the treasurer goes after this, with what is already there.
  const fiscalYear = currentFiscalYear(calendarQuery.data)
  const openCount = fiscalYear?.periods.filter((period) => period.status === FiscalPeriodStatus.Open).length ?? 0
  const closedCount = fiscalYear?.periods.filter((period) => period.status === FiscalPeriodStatus.Closed).length ?? 0
  const periodsLine = fiscalYear
    ? t("next.periods", {
        year: fiscalYear.label,
        open: t(pluralKey("next.open", openCount), { count: openCount }),
        closed: t(pluralKey("next.closed", closedCount, { hasNone: true }), { count: closedCount }),
      })
    : calendarQuery.data
      ? t("next.noPeriods")
      : null
  const openingsSet = openingQuery.data ? openingQuery.data.accounts.filter((account) => account.set).length : null
  const openingsLine = openingsSet === null ? null : t(pluralKey("next.openings", openingsSet, { hasNone: true }), { count: openingsSet })
  const scopedBranchSlug = activeContext === "parish" ? parishBranch?.slug ?? branchSlug ?? "" : branchSlug ?? ""
  const tabHref = (tab: FinanceSettingsTab) =>
    `${buildBranchAdminUrl(scopedBranchSlug, `/finance/settings/${tab}`, workspaceSlug)}${search}`

  return (
    <div className='flex flex-col gap-4' data-testid='finance-setup' data-locked={locked ? "true" : "false"}>
      <FinanceSettingsSummaryCard
        testId='finance-setup-summary'
        mark={
          locked ? (
            <LockIcon className='size-[22px]' />
          ) : status.isComplete ? (
            <CheckCircle2Icon className='size-[22px]' />
          ) : (
            <SettingsIcon className='size-[22px]' />
          )
        }
        title={status.isComplete ? t("summary.titleComplete") : t("summary.titleIncomplete")}
        pills={[
          status.isComplete
            ? { id: "status", tone: AlivePillTone.Success, label: t("badge.complete") }
            : { id: "status", tone: AlivePillTone.Warning, label: t("badge.incomplete") },
        ]}
        description={description}
        figures={
          status.isComplete
            ? [
                {
                  id: "entries",
                  value: status.postedEntries.toLocaleString(locale),
                  label: t(pluralKey("summary.entries", status.postedEntries)),
                },
                ...(monthsKept !== null
                  ? [{ id: "months", value: monthsKept.toLocaleString(locale), label: t(pluralKey("summary.months", monthsKept)) }]
                  : []),
              ]
            : []
        }
      />

      <FinanceSettingsTileGrid columns={4}>
        <FinanceSettingsTile
          testId='finance-setup-tile-currency'
          label={t("fields.currency")}
          locked={!currencyEditable}
          lockedLabel={lockedLabel}
          value={labelOf(CURRENCY_OPTIONS, currency)}
          editor={currencyEditable ? select(t("fields.currency"), CURRENCY_OPTIONS, currency, setCurrency) : undefined}
          hint={fiscalYearInherited ? t("tiles.inherited") : t("tiles.currencyHint")}
        />
        <FinanceSettingsTile
          testId='finance-setup-tile-timezone'
          label={t("tiles.timezone")}
          locked={!timezoneEditable}
          lockedLabel={lockedLabel}
          value={timezone}
          editor={timezoneEditable ? select(t("tiles.timezone"), TIMEZONE_OPTIONS, timezone, setTimezone, true) : undefined}
          hint={city ? t("tiles.timezoneHint", { city }) : t("hints.timezone")}
        />
        <FinanceSettingsTile
          testId='finance-setup-tile-fiscal-year'
          label={t("fields.fiscalYearStart")}
          locked={!startMonthEditable}
          lockedLabel={lockedLabel}
          value={labelOf(MONTH_OPTIONS, startMonth)}
          editor={startMonthEditable ? select(t("fields.fiscalYearStart"), MONTH_OPTIONS, startMonth, setStartMonth) : undefined}
          hint={
            fiscalYearInherited
              ? t("tiles.inherited")
              : t("tiles.fiscalYearHint", { start: monthInSentence(startIndex), end: monthInSentence(Number(endMonth)) })
          }
        />
        <FinanceSettingsTile
          testId='finance-setup-tile-conversion'
          label={t("fields.conversionDate")}
          locked={!dateEditable}
          lockedLabel={lockedLabel}
          value={conversionMonthYear ? conversionMonthYear.charAt(0).toLocaleUpperCase(locale) + conversionMonthYear.slice(1) : null}
          editor={
            dateEditable ? (
              // A conversion date is a period boundary, not a specific day: Month + Year, always the 1st.
              <div className='grid grid-cols-[minmax(0,1fr)_minmax(0,6rem)] gap-2'>
                {select(t("fields.conversionMonth"), MONTH_OPTIONS, conversionMonth, (value) =>
                  setConversionDate(toConversionDay(conversionYear ?? String(CURRENT_YEAR), value)),
                )}
                {select(t("fields.conversionYear"), YEAR_OPTIONS, conversionYear, (value) =>
                  setConversionDate(toConversionDay(value, conversionMonth ?? "1")),
                )}
              </div>
            ) : undefined
          }
          // The Xero convention: opening balances close the old books on the last day of the prior month.
          hint={openingAsAt ? t("tiles.conversionHint", { date: openingAsAt }) : t("hints.conversionDate")}
        />
      </FinanceSettingsTileGrid>

      {!locked ? (
        <div className='flex justify-end'>
          <Button className='w-full rounded-full sm:w-auto' onClick={() => void onSubmit()} disabled={!canSubmit}>
            {completeMutation.isPending ? t("actions.saving") : t("actions.save")}
          </Button>
        </div>
      ) : null}

      {showNext ? (
        <SurfaceCard className='flex flex-col gap-3 px-5 py-3.5 sm:flex-row sm:items-center' data-testid='finance-setup-next'>
          <p className='min-w-0 flex-1 text-[13.5px]'>
            <span className='font-bold'>{t("next.title")}</span>
            {periodsLine || openingsLine ? (
              <span className='text-muted-foreground'> · {[periodsLine, openingsLine].filter(Boolean).join(", ")}</span>
            ) : null}
          </p>
          <div className='flex flex-wrap gap-2'>
            <Button asChild variant='outline' size='sm' className='rounded-full'>
              <Link to={tabHref(FinanceSettingsTab.FiscalPeriods)}>{t("next.viewPeriods")}</Link>
            </Button>
            <Button asChild variant='outline' size='sm' className='rounded-full'>
              <Link to={tabHref(FinanceSettingsTab.OpeningBalances)}>{t("next.viewOpenings")}</Link>
            </Button>
          </div>
        </SurfaceCard>
      ) : null}
    </div>
  )
}
